import React, { useEffect, useState, useCallback, useMemo } from "react";
import ReactApexChart from "react-apexcharts";
import "./folders.css";

const CircularProgressChart = React.memo(({ percentage, avatar, name, size = "large" }) => {
  const validPercentage = Number(percentage) || 0;

  const getColor = useCallback((percentage) => {
    if (percentage >= 80) return "#4CAF50";
    if (percentage >= 50) return "#FFC107";
    return "#F44336";
  }, []);

  const chartSize = size === "small" ? 90 : 150;
  const fontSize = size === "small" ? "20px" : "25px";
  const hollowSize = size === "small" ? "45%" : "60%";

  const options = useMemo(
    () => ({
      chart: { type: "radialBar" },
      plotOptions: {
        radialBar: {
          hollow: { size: hollowSize },
          dataLabels: { name: { show: false }, value: { show: false } },
        },
      },
      fill: { colors: [getColor(validPercentage)] },
      stroke: { lineCap: "round" },
    }),
    [hollowSize, getColor, validPercentage]
  );

  return (
    <div className="radial-chart-container" style={{ width: chartSize, height: chartSize, position: "relative" }}>
      <ReactApexChart options={options} series={[validPercentage]} type="radialBar" height={chartSize} width={chartSize} />
      <div
        className="chart-percentagez"
        style={{
          position: "absolute",
          top: "100%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          fontSize,
          fontWeight: "bold",
          color: "#000",
          zIndex: 2,
        }}
      >
        {validPercentage.toFixed(2)}%
      </div>
      {size === "large" && (
        <div
          className="chart-contentz"
          style={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            zIndex: 1,
          }}
        >
          <img
            src={avatar}
            alt={name}
            className="chart-avatar"
            style={{
              width: chartSize * 0.4,
              height: chartSize * 0.4,
              borderRadius: "50%",
              objectFit: "cover",
              border: "2px solid white",
            }}
          />
        </div>
      )}
    </div>
  );
});

const ComandesFolders = () => {
    const [commercialData, setCommercialData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [timer, setTimer] = useState(60);
    const proxyBase = "https://corss-smoky.vercel.app/api/proxy?url=";
  
    const fetchData = useCallback(async () => {
      try {
        const dataUrl = "https://test.eurodistech.com/storage/json/compabilite.json";
  
        const response = await fetch(`${proxyBase}${encodeURIComponent(dataUrl)}`);
        if (!response.ok) throw new Error("Failed to fetch data");
  
        const data = await response.json();
        if (data?.passation_commandes) {
          setCommercialData(data.passation_commandes);
        }
      } catch (error) {
        setError(error.message);
      } finally {
        setLoading(false);
      }
    }, []);
  
    useEffect(() => {
      fetchData();
    }, [fetchData]);
  
    useEffect(() => {
      const interval = setInterval(() => {
        setTimer((prevTimer) => {
          if (prevTimer === 1) {
            fetchData();
            return 60; // Reset timer
          }
          return prevTimer - 1;
        });
      }, 1000);
  
      return () => clearInterval(interval);
    }, [fetchData]);
  
    const getColor = useCallback((percentage) => {
      if (percentage >= 80) return "#4CAF50";
      if (percentage >= 50) return "#FFC107";
      return "#F44336";
    }, []);
  
    const sortedCommercialDataWeek = useMemo(() => {
      return [...commercialData].sort((a, b) => {
        const tauxA = ((a?.livred_week_str ? a.livred_week_str.split(",").filter(Boolean).length : 0) * 100) /
                      ((a?.total_week_str ? a.total_week_str.split(",").filter(Boolean).length : 1) || 1);
        const tauxB = ((b?.livred_week_str ? b.livred_week_str.split(",").filter(Boolean).length : 0) * 100) /
                      ((b?.total_week_str ? b.total_week_str.split(",").filter(Boolean).length : 1) || 1);
        return tauxB - tauxA;
      });
    }, [commercialData]);
  
    const sortedCommercialDataMonth1 = useMemo(() => {
      return [...commercialData].sort((a, b) => {
        const tauxA = (a?.livred_month_1 || 0) / (a?.total_month_1 || 1) * 100;
        const tauxB = (b?.livred_month_1 || 0) / (b?.total_month_1 || 1) * 100;
        return tauxB - tauxA;
      });
    }, [commercialData]);
  
    const sortedCommercialDataMonth = useMemo(() => {
      return [...commercialData].sort((a, b) => {
        const tauxA = (a?.livred_month || 0) / (a?.total_month || 1) * 100;
        const tauxB = (b?.livred_month || 0) / (b?.total_month || 1) * 100;
        return tauxB - tauxA;
      });
    }, [commercialData]);
  
    const overallTauxweek = useMemo(() => {
      if (sortedCommercialDataWeek.length === 0) return "0.00";
  
      const totalTaux = sortedCommercialDataWeek.reduce((acc, member) => {
        const dvCurrentDay = member?.livred_week_str?.split(",").filter(Boolean).map(Number) || [];
        const dpCurrentDay = member?.total_week_str?.split(",").filter(Boolean).map(Number) || [];
        return acc + (dpCurrentDay.length > 0 ? (dvCurrentDay.length / dpCurrentDay.length) * 100 : 0);
      }, 0);
  
      return Number(totalTaux / sortedCommercialDataWeek.length).toFixed(2);
    }, [sortedCommercialDataWeek]);
  
    const overallTauxmonth_1 = useMemo(() => {
      if (sortedCommercialDataMonth1.length === 0) return "0.00";
  
      const totalTaux = sortedCommercialDataMonth1.reduce((acc, member) => {
        const dvCurrentDay = member?.livred_month_1;
        const dpCurrentDay = member?.total_month_1;
        return acc + (dpCurrentDay > 0 ? (dvCurrentDay / dpCurrentDay) * 100 : 0);
      }, 0);
  
      return Number(totalTaux / sortedCommercialDataMonth1.length).toFixed(2);
    }, [sortedCommercialDataMonth1]);
  
    const overallTauxmonth = useMemo(() => {
      if (sortedCommercialDataMonth.length === 0) return "0.00";
  
      const totalTaux = sortedCommercialDataMonth.reduce((acc, member) => {
        const dvCurrentDay = member?.livred_month;
        const dpCurrentDay = member?.total_month;
        return acc + (dpCurrentDay > 0 ? (dvCurrentDay / dpCurrentDay) * 100 : 0);
      }, 0);
  
      return Number(totalTaux / sortedCommercialDataMonth.length).toFixed(2);
    }, [sortedCommercialDataMonth]);
  
    if (loading) return <div className="loading">Loading...</div>;
    if (error) return <div className="error">Error: {error}</div>;
    if (!commercialData.length) return <div className="no-data">No data available</div>;
  
    const renderBadge = (id, type, index) => {
      return (
        <span
          key={index}
          style={{
            padding: "5px 10px",
            backgroundColor: type === "envoyer" ? "#4CAF50" : "#FFC107", // Different colors based on type
            color: "white",
            borderRadius: "12px",
            fontSize: "16px",
          }}
        >
          {id}
        </span>
      );
    };
  
    return (
      <div>
        <h1>WEEK</h1>
        <div className="Apps">
          <div className="table-wrapper">
            <table>
              <thead>
                <tr>
                  <th></th>
                  <th>Taux</th>
                  <th>COMMANDE À LIVRE</th>
                  <th>COMMANDE LIVRE</th>
                  <th>NC</th>
                </tr>
              </thead>
              <tbody>
                {sortedCommercialDataWeek.map((member) => {
                  const dvCurrentDay = member?.livred_week_str ? member.livred_week_str.split(",").map(item => item.trim()).filter(Boolean) : [];
                  const dpCurrentDay = member?.total_week_str ? member.total_week_str.split(",").map(item => item.trim()).filter(Boolean) : [];
  
                  const dpCurrentDayFiltered = dpCurrentDay.filter(item => !dvCurrentDay.some(dvItem => dvItem.toLowerCase() === item.toLowerCase()));
  
                  const taux = dpCurrentDay.length > 0 ? ((dpCurrentDay.length - dpCurrentDayFiltered.length) / dpCurrentDay.length) * 100 : 0;
  
                  const nc = member?.nc_week;
  
                  return (
                    <tr key={member.id_user}>
                      <td>
                        <img
                          className="imgavatar"
                          src={`https://www.eurodistech.com/erp/public/assets/images/avatars/${member.avatar}`}
                          alt={member.full_name}
                          style={{
                            width: "70px",
                            height: "70px",
                            borderRadius: "50%",
                            objectFit: "cover",
                            border: "2px solid white",
                          }}
                        />
                      </td>
                      <td style={{ background: `linear-gradient(to right, ${getColor(taux)}, white)`, fontWeight: "bold", fontSize: '17px' }}>
                        {taux.toFixed(2)}%
                      </td>
                      <td>
                        {dpCurrentDayFiltered.length > 0 ? (
                          <div style={{ display: "flex", flexDirection: "row", gap: 3, flexWrap: "wrap", paddingLeft: 5 }}>
                            {dpCurrentDayFiltered.map((id, index) => renderBadge(id, "dp", index))}
                          </div>
                        ) : (
                          ""
                        )}
                      </td>
                      <td>
                        {dvCurrentDay.length > 0 ? (
                          <div style={{ display: "flex", flexDirection: "row", gap: 3, flexWrap: "wrap", paddingLeft: 5 }}>
                            {dvCurrentDay.map((id, index) => renderBadge(id, "envoyer", index))}
                          </div>
                        ) : (
                          ""
                        )}
                      </td>
                      <td style={{ fontSize: '21px', fontWeight: 'bold' }}>
                        {nc}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <CircularProgressChart
            percentage={overallTauxweek}
            avatar="https://www.eurodistech.com/erp/public/assets/images/avatars/khalouk-sanaa-44-thumb.jpg"
            name="Sanae KHALOUK"
          />
          <CircularProgressChart
            percentage={overallTauxweek}
            avatar="https://www.eurodistech.com/erp/public/assets/images/avatars/tazi-charafa-49-thumb.jpg"
            name="charafa tazi"
          />
          <CircularProgressChart
            percentage={overallTauxweek}
            avatar="https://www.eurodistech.com/erp/public/assets/images/avatars/hounaida-qotbi-85-thumb.jpg"
            name="honnaida"
          />
        </div>
  
        <h1>Month - 1</h1>
        <div className="Apps">
          <div className="table-wrapper">
            <table>
              <thead>
                <tr>
                  <th></th>
                  <th>Taux</th>
                  <th>COMMANDE À LIVRE</th>
                  <th>COMMANDE LIVRE</th>
                  <th>NC</th>
                </tr>
              </thead>
              <tbody>
                {sortedCommercialDataMonth1.map((member) => {
                  const dvCurrentDay = member?.livred_month_1 || 0;
                  const dpCurrentDay = member?.total_month_1 || 0;
  
                  const dpCurrentDayFiltered = dpCurrentDay - dvCurrentDay;
                  const taux = dpCurrentDay > 0 ? (dvCurrentDay / dpCurrentDay) * 100 : 0;
                  const nc = member?.nc_month_1;
  
                  return (
                    <tr key={member.id_user}>
                      <td>
                        <img
                          className="imgavatar"
                          src={`https://www.eurodistech.com/erp/public/assets/images/avatars/${member.avatar}`}
                          alt={member.full_name}
                          style={{
                            width: "70px",
                            height: "70px",
                            borderRadius: "50%",
                            objectFit: "cover",
                            border: "2px solid white",
                          }}
                        />
                      </td>
                      <td style={{ background: `linear-gradient(to right, ${getColor(taux)}, white)`, fontWeight: "bold", fontSize: '17px' }}>
                        {taux.toFixed(2)}%
                      </td>
                      <td style={{ fontWeight: "bold", fontSize: '17px' }}>
                        {dpCurrentDayFiltered}
                      </td>
                      <td style={{ fontWeight: "bold", fontSize: '17px' }}>
                        {dvCurrentDay}
                      </td>
                      <td style={{ fontSize: '21px', fontWeight: 'bold' }}>
                        {nc}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <CircularProgressChart
            percentage={overallTauxmonth_1}
            avatar="https://www.eurodistech.com/erp/public/assets/images/avatars/khalouk-sanaa-44-thumb.jpg"
            name="Sanae KHALOUK"
          />
          <CircularProgressChart
            percentage={overallTauxmonth_1}
            avatar="https://www.eurodistech.com/erp/public/assets/images/avatars/tazi-charafa-49-thumb.jpg"
            name="charafa tazi"
          />
          <CircularProgressChart
            percentage={overallTauxmonth_1}
            avatar="https://www.eurodistech.com/erp/public/assets/images/avatars/hounaida-qotbi-85-thumb.jpg"
            name="honnaida"
          />
        </div>
  
        <h1>Month</h1>
        <div className="Apps">
          <div className="table-wrapper">
            <table>
              <thead>
                <tr>
                  <th></th>
                  <th>Taux</th>
                  <th>COMMANDE À LIVRE</th>
                  <th>COMMANDE LIVRE</th>
                  <th>NC</th>
                </tr>
              </thead>
              <tbody>
                {sortedCommercialDataMonth.map((member) => {
                  const dvCurrentDay = member?.livred_month || 0;  // Default to 0 if undefined or null
                const dpCurrentDay = member?.total_month || 0;  // Default to 0 if undefined or null

                // Subtract dvCurrentDay from dpCurrentDay
                const dpCurrentDayFiltered = dpCurrentDay - dvCurrentDay;
                const taux = dpCurrentDay > 0 ? (dvCurrentDay / dpCurrentDay) * 100 : 0;
                const nc=member?.nc_month;

                return (
                  <tr key={member.id_user}>
                    <td>
                    <img
                        className="imgavatar"
                        src={`https://www.eurodistech.com/erp/public/assets/images/avatars/${member.avatar}`}
                        alt={member.full_name}
                        style={{
                        width: "70px", // Increase the width as needed
                        height: "70px", // Increase the height as needed
                        borderRadius: "50%",
                        objectFit: "cover",
                        border: "2px solid white", // Optional border
                        }}
                    />
                    </td>
                    <td style={{ background: `linear-gradient(to right, ${getColor(taux)}, white)`, fontWeight: "bold",fontSize:'17px' }}>
                      {taux.toFixed(2)}%
                    </td>
                    <td style={{ fontWeight: "bold",fontSize:'17px' }}>
                        {dpCurrentDayFiltered}
                        </td>
                    <td style={{ fontWeight: "bold",fontSize:'17px' }}>
                        {dvCurrentDay}
                        </td>
                    <td  style={{fontSize:'21px',fontWeight:'bold'}}>
                        {nc}
                    </td>

                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <CircularProgressChart
          percentage={overallTauxmonth}
            avatar="https://www.eurodistech.com/erp/public/assets/images/avatars/khalouk-sanaa-44-thumb.jpg"
            name="Sanae KHALOUK"
        />
                <CircularProgressChart
          percentage={overallTauxmonth}
          avatar="https://www.eurodistech.com/erp/public/assets/images/avatars/tazi-charafa-49-thumb.jpg"
          name="charafa tazi"
        />
                <CircularProgressChart
          percentage={overallTauxmonth}
          avatar="https://www.eurodistech.com/erp/public/assets/images/avatars/hounaida-qotbi-85-thumb.jpg"
          name="honnaida"
        />
      </div>
      
      <div className="timer">
        <p>Page will refresh in <strong style={{ color: "red" }}>{timer}</strong> seconds...</p>
      </div>

      
    </div>

    
  );
};

export default ComandesFolders;
